import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { StructuralPieceId, UnitTypeId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import type { ItemFactor } from './own-factor';
import { pieceNameTooLong, pieceNameTooLongMessage } from './pile';
import { isValidOutline, polygonArea, type Point } from './polygon';
import type { SteelRate } from './steel';

/**
 * A escada de concreto (Fase 27, etapa 5 — D10, F17, RN-373).
 *
 * Desenhada como a obra a lê na planta: o **contorno de cada lance** e de cada **patamar**, e,
 * no lance, os degraus — quantos, o espelho, o piso — e a espessura da laje inclinada. A conta
 * sai daí: o concreto da laje inclinada mais o dos degraus, a fôrma de fundo inclinada, dos
 * espelhos e das laterais, e a projeção que o escoramento cobre.
 *
 * É da área comum do desenho do pavimento, como a laje (F3). Não é o ambiente ESCADA, que
 * continua medindo o acabamento — piso, pintura, corrimão (F17): os dois se desenham um sobre
 * o outro e cada um mede o seu.
 *
 * > **A regra em uma linha (RN-373):** a escada é a única peça que se corrige **sem motivo** —
 * > a conta é aproximada (o degrau ingrauxido, o lance em leque), e corrigir é o normal.
 */

export interface StairFlight {
  /** O contorno do lance em planta — o retângulo da projeção, em metros. */
  readonly outline: readonly Point[];
  /** Quantos degraus (espelhos) o lance tem. */
  readonly steps: number;
  /** O espelho (altura do degrau), em metros. 0,175 é o comum. */
  readonly riser: number;
  /** O piso (profundidade do degrau), em metros. 0,28 é o comum. */
  readonly tread: number;
  /** A espessura da laje inclinada, em metros — medida na perpendicular. */
  readonly thickness: number;
}

export interface StairLanding {
  readonly outline: readonly Point[];
  /** Metros. */
  readonly thickness: number;
}

export interface Stair {
  readonly id: StructuralPieceId;
  readonly unitTypeId: UnitTypeId;
  readonly kind: 'ESCADA';
  /** "E1" — único entre as escadas do desenho. */
  readonly name: string;
  readonly flights: readonly StairFlight[];
  readonly landings: readonly StairLanding[];
  /**
   * O fator próprio de cada item nesta escada, por cima do índice da obra (Fase 42, etapa 3 —
   * G3; RN-571). Sem tipo: a escada não tem. Ausente: o rascunho de antes; vazio: nenhum.
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria desta escada, kg/m³ por bitola (Fase 42, etapa 3 — RN-572). Vale
   * inteira, por cima da da disciplina (a do elemento, RN-573). Ausente: o rascunho de antes;
   * vazia: nenhuma.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export const STAIR_ERROR = {
  NAME_REQUIRED: 'TAKEOFF.STAIR_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.STAIR_NAME_TAKEN',
  NAME_TOO_LONG: 'TAKEOFF.STAIR_NAME_TOO_LONG',
  EMPTY: 'TAKEOFF.STAIR_EMPTY',
  INVALID_OUTLINE: 'TAKEOFF.STAIR_INVALID_OUTLINE',
  INVALID_FLIGHT: 'TAKEOFF.STAIR_INVALID_FLIGHT',
  INVALID_THICKNESS: 'TAKEOFF.STAIR_INVALID_THICKNESS',
} as const;

const AREA_MINIMA = 0.0001;

function positivo(valor: number): boolean {
  return Number.isFinite(valor) && valor > 0;
}

function tres(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

function contornoValido(outline: readonly Point[]): boolean {
  return isValidOutline(outline) && polygonArea(outline) >= AREA_MINIMA;
}

export function checkStairFields(
  fields: {
    readonly name: string;
    readonly flights: readonly StairFlight[];
    readonly landings: readonly StairLanding[];
  },
  siblings: readonly Pick<Stair, 'id' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<
  {
    readonly name: string;
    readonly flights: readonly StairFlight[];
    readonly landings: readonly StairLanding[];
  },
  DomainError
> {
  const nome = fields.name.trim();
  if (nome === '') {
    return err(domainError(STAIR_ERROR.NAME_REQUIRED, 'O nome da escada é obrigatório.'));
  }
  if (pieceNameTooLong(nome)) {
    return err(domainError(STAIR_ERROR.NAME_TOO_LONG, pieceNameTooLongMessage('escada')));
  }
  const chave = normalizeName(nome);
  if (
    siblings.some(
      (outra) =>
        outra.deletedAt === undefined && outra.id !== selfId && normalizeName(outra.name) === chave,
    )
  ) {
    return err(
      domainError(STAIR_ERROR.NAME_TAKEN, `Já existe a escada ${nome} neste desenho.`, {
        name: nome,
      }),
    );
  }
  if (fields.flights.length === 0) {
    return err(domainError(STAIR_ERROR.EMPTY, `A escada ${nome} precisa de ao menos um lance.`));
  }
  for (const [indice, lance] of fields.flights.entries()) {
    const rotulo = `O lance ${String(indice + 1)} da escada ${nome}`;
    if (!contornoValido(lance.outline)) {
      return err(
        domainError(STAIR_ERROR.INVALID_OUTLINE, `${rotulo} precisa de contorno com área.`),
      );
    }
    if (
      !Number.isInteger(lance.steps) ||
      lance.steps < 1 ||
      !positivo(lance.riser) ||
      !positivo(lance.tread)
    ) {
      return err(
        domainError(
          STAIR_ERROR.INVALID_FLIGHT,
          `${rotulo} precisa de ao menos um degrau, com espelho e piso maiores que zero.`,
        ),
      );
    }
    if (!positivo(lance.thickness)) {
      return err(
        domainError(
          STAIR_ERROR.INVALID_THICKNESS,
          `${rotulo} precisa de espessura maior que zero.`,
        ),
      );
    }
  }
  for (const [indice, patamar] of fields.landings.entries()) {
    const rotulo = `O patamar ${String(indice + 1)} da escada ${nome}`;
    if (!contornoValido(patamar.outline)) {
      return err(
        domainError(STAIR_ERROR.INVALID_OUTLINE, `${rotulo} precisa de contorno com área.`),
      );
    }
    if (!positivo(patamar.thickness)) {
      return err(
        domainError(
          STAIR_ERROR.INVALID_THICKNESS,
          `${rotulo} precisa de espessura maior que zero.`,
        ),
      );
    }
  }
  return ok({
    name: nome,
    flights: fields.flights.map((lance) => ({
      outline: lance.outline,
      steps: lance.steps,
      riser: tres(lance.riser),
      tread: tres(lance.tread),
      thickness: tres(lance.thickness),
    })),
    landings: fields.landings.map((patamar) => ({
      outline: patamar.outline,
      thickness: tres(patamar.thickness),
    })),
  });
}

// ── A geometria de um lance ─────────────────────────────────────────────────────

/** O comprimento horizontal do lance: degraus × piso. */
function horizontal(lance: StairFlight): number {
  return lance.steps * lance.tread;
}

/** A altura que o lance vence: degraus × espelho. */
export function flightRise(lance: StairFlight): number {
  return lance.steps * lance.riser;
}

/**
 * A largura do lance: a área da projeção dividida pelo comprimento horizontal. Sai do
 * desenho, e não de mais um número digitado — o contorno já diz quanto o lance é largo.
 */
export function flightWidth(lance: StairFlight): number {
  const comprimento = horizontal(lance);
  return comprimento > 0 ? polygonArea(lance.outline) / comprimento : 0;
}

/** O comprimento da laje inclinada: a hipotenusa do horizontal e da altura. */
export function flightSlope(lance: StairFlight): number {
  return Math.hypot(horizontal(lance), flightRise(lance));
}

// ── As contas da escada ─────────────────────────────────────────────────────────

type EscadaDaConta = Pick<Stair, 'flights' | 'landings'>;

/** A projeção em planta — lances e patamares —, o que o escoramento cobre. m², duas casas. */
export function stairProjection(stair: EscadaDaConta): number {
  const area =
    stair.flights.reduce((soma, lance) => soma + polygonArea(lance.outline), 0) +
    stair.landings.reduce((soma, patamar) => soma + polygonArea(patamar.outline), 0);
  return roundQuantity(area, 2);
}

/**
 * O concreto: em cada lance, a laje inclinada (largura × comprimento inclinado × espessura) mais
 * os degraus (largura × degraus × espelho × piso ÷ 2); em cada patamar, área × espessura.
 * m³, três casas.
 */
export function stairVolume(stair: EscadaDaConta): number {
  const dosLances = stair.flights.reduce((soma, lance) => {
    const largura = flightWidth(lance);
    const laje = largura * flightSlope(lance) * lance.thickness;
    const degraus = (largura * lance.steps * lance.riser * lance.tread) / 2;
    return soma + laje + degraus;
  }, 0);
  const dosPatamares = stair.landings.reduce(
    (soma, patamar) => soma + polygonArea(patamar.outline) * patamar.thickness,
    0,
  );
  return roundQuantity(dosLances + dosPatamares, 3);
}

/**
 * A fôrma: no lance, o fundo inclinado, os espelhos e as duas laterais (o triângulo dos degraus
 * e a faixa da laje inclinada); no patamar, o fundo. m², duas casas.
 */
export function stairFormwork(stair: EscadaDaConta): number {
  const dosLances = stair.flights.reduce((soma, lance) => {
    const largura = flightWidth(lance);
    const inclinado = flightSlope(lance);
    const fundo = largura * inclinado;
    const espelhos = largura * lance.riser * lance.steps;
    const lateral = (lance.steps * lance.riser * lance.tread) / 2 + inclinado * lance.thickness;
    return soma + fundo + espelhos + 2 * lateral;
  }, 0);
  const dosPatamares = stair.landings.reduce(
    (soma, patamar) => soma + polygonArea(patamar.outline),
    0,
  );
  return roundQuantity(dosLances + dosPatamares, 2);
}

/** Quantos degraus a escada tem — a soma dos lances. */
export function stairSteps(stair: Pick<Stair, 'flights'>): number {
  return stair.flights.reduce((soma, lance) => soma + lance.steps, 0);
}
